/**
 * NutrientComparisonGraph.tsx
 * 
 * Component for comparing multiple nutrient profiles side-by-side.
 * Allows users to compare different recipes, growth stages, or crop types.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  Tooltip,
  CircularProgress,
  SelectChangeEvent,
  Button,
  Chip,
  Stack,
  IconButton,
  useTheme,
} from '@mui/material';
import Grid from '../../components/common/Grid';
import ReactEcharts from 'echarts-for-react';
import { NutrientProfile } from '../../types/nutrient';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

interface ProfileData {
  id: string;
  name: string;
  profile: NutrientProfile;
  color?: string;
}

interface NutrientComparisonGraphProps {
  title?: string;
  profiles?: ProfileData[];
  onAddProfile?: () => void;
  onRemoveProfile?: (id: string) => void;
  loading?: boolean;
  error?: string | null;
  allowDownload?: boolean;
  onSaveChart?: (dataUrl: string) => void;
}

// Predefined colors for chart series
const DEFAULT_COLORS = [
  '#5470c6', '#91cc75', '#fac858', '#ee6666',
  '#73c0de', '#3ba272', '#fc8452', '#9a60b4',
  '#ea7ccc'
];

// Element categories
const ELEMENT_CATEGORIES = {
  macronutrients: ['N', 'P', 'K', 'Ca', 'Mg', 'S'],
  micronutrients: ['Fe', 'Mn', 'Cu', 'Zn', 'B', 'Mo', 'Cl']
};

const NutrientComparisonGraph: React.FC<NutrientComparisonGraphProps> = ({
  title = 'Nutrient Profile Comparison',
  profiles = [],
  onAddProfile,
  onRemoveProfile,
  loading = false,
  error = null,
  allowDownload = true,
  onSaveChart,
}) => {
  const theme = useTheme();
  
  const [chartType, setChartType] = useState<'bar' | 'line' | 'radar'>('bar');
  const [displayUnit, setDisplayUnit] = useState<string>('ppm');
  const [selectedCategory, setSelectedCategory] = useState<string>('macro');
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [chartInstance, setChartInstance] = useState<any>(null);
  const [normalizeData, setNormalizeData] = useState<boolean>(false);
  
  // Get all unique elements across all profiles
  const uniqueElements = useMemo(() => {
    const elements = new Set<string>();
    
    profiles.forEach(profile => {
      Object.keys(profile.profile).forEach(element => {
        // Only consider numeric properties as elements
        if (typeof profile.profile[element] === 'number') {
          elements.add(element);
        }
      });
    });
    
    return Array.from(elements);
  }, [profiles]);
  
  // Filter elements based on selected category
  const filteredElements = useMemo(() => {
    if (selectedCategory === 'all') {
      return uniqueElements;
    }
    
    if (selectedCategory === 'macro') {
      return uniqueElements.filter(element => 
        ELEMENT_CATEGORIES.macronutrients.includes(element)
      );
    }
    
    if (selectedCategory === 'micro') {
      return uniqueElements.filter(element => 
        ELEMENT_CATEGORIES.micronutrients.includes(element)
      );
    }
    
    return uniqueElements;
  }, [uniqueElements, selectedCategory]);
  
  // Function to safely get numerical value from profile
  const getNumericValue = (profile: ProfileData, element: string): number => {
    if (
      profile && 
      profile.profile && 
      typeof profile.profile[element] === 'number'
    ) {
      return profile.profile[element] as number;
    }
    return 0;
  };
  
  // Generate chart options based on selected chart type and data
  const getChartOptions = () => {
    if (profiles.length === 0 || filteredElements.length === 0) {
      return {
        title: {
          text: 'No Data Available',
          left: 'center',
        }
      };
    }
    
    // For single element comparison view
    if (selectedElement) {
      const elementData = profiles.map(profile => ({
        name: profile.name,
        value: getNumericValue(profile, selectedElement),
        color: profile.color || DEFAULT_COLORS[profiles.indexOf(profile) % DEFAULT_COLORS.length]
      }));
      
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: elementData.map(item => item.name),
          axisLabel: {
            interval: 0,
            rotate: 45
          }
        },
        yAxis: {
          type: 'value',
          name: `${selectedElement} (${displayUnit})`
        },
        series: [
          {
            name: selectedElement,
            type: 'bar',
            data: elementData.map(item => item.value),
            itemStyle: {
              color: (params: any) => elementData[params.dataIndex].color
            }
          }
        ]
      };
    }
    
    // For bar chart comparison
    if (chartType === 'bar') {
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        legend: {
          data: profiles.map(profile => profile.name),
          bottom: 0,
          type: profiles.length > 5 ? 'scroll' : 'plain'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: 10 + (Math.min(profiles.length, 3) * 20), // Adjust bottom margin based on legend height
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: filteredElements,
          axisLabel: {
            interval: 0,
            rotate: 45
          }
        },
        yAxis: {
          type: 'value',
          name: displayUnit,
          nameLocation: 'end'
        },
        series: profiles.map((profile, index) => ({
          name: profile.name,
          type: 'bar',
          data: filteredElements.map(element => {
            const value = getNumericValue(profile, element);
            
            if (normalizeData && profiles.length > 0) {
              // Find max value for this element across all profiles
              const maxValue = Math.max(...profiles.map(p => getNumericValue(p, element)));
              return maxValue ? (value / maxValue) * 100 : 0;
            }
            
            return value;
          }),
          itemStyle: {
            color: profile.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]
          }
        }))
      };
    } else if (chartType === 'line') {
      // Line chart for comparing trends
      return {
        tooltip: {
          trigger: 'axis'
        },
        legend: {
          data: profiles.map(profile => profile.name),
          bottom: 0,
          type: profiles.length > 5 ? 'scroll' : 'plain'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: 10 + (Math.min(profiles.length, 3) * 20),
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: filteredElements,
          axisLabel: {
            interval: 0,
            rotate: 45
          }
        },
        yAxis: {
          type: 'value',
          name: displayUnit,
          nameLocation: 'end'
        },
        series: profiles.map((profile, index) => ({
          name: profile.name,
          type: 'line',
          data: filteredElements.map(element => {
            const value = getNumericValue(profile, element);
            
            if (normalizeData && profiles.length > 0) {
              // Find max value for this element across all profiles
              const maxValue = Math.max(...profiles.map(p => getNumericValue(p, element)));
              return maxValue ? (value / maxValue) * 100 : 0;
            }
            
            return value;
          }),
          smooth: true,
          symbolSize: 5,
          lineStyle: {
            width: 2
          },
          itemStyle: {
            color: profile.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]
          }
        }))
      };
    } else if (chartType === 'radar') {
      // Radar chart for overall profile comparison
      // Find max values for each element to set radar chart scale
      const maxValues = filteredElements.reduce((acc, element) => {
        let maxValue = Math.max(...profiles.map(profile => getNumericValue(profile, element)));
        // Add a little padding for better visualization
        maxValue = maxValue * 1.2;
        return { ...acc, [element]: maxValue };
      }, {} as Record<string, number>);
      
      return {
        tooltip: {
          trigger: 'item'
        },
        legend: {
          data: profiles.map(profile => profile.name),
          bottom: 0,
          type: profiles.length > 5 ? 'scroll' : 'plain'
        },
        radar: {
          indicator: filteredElements.map(element => ({
            name: element,
            max: maxValues[element]
          })),
          center: ['50%', '50%'],
          radius: '60%'
        },
        series: [
          {
            type: 'radar',
            data: profiles.map((profile, index) => ({
              value: filteredElements.map(element => {
                const value = getNumericValue(profile, element);
                
                if (normalizeData && profiles.length > 0) {
                  // Find max value for this element across all profiles
                  const maxValue = Math.max(...profiles.map(p => getNumericValue(p, element)));
                  return maxValue ? (value / maxValue) * 100 : 0;
                }
                
                return value;
              }),
              name: profile.name,
              areaStyle: {
                opacity: 0.1
              },
              lineStyle: {
                width: 2
              },
              itemStyle: {
                color: profile.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]
              }
            }))
          }
        ]
      };
    }
    
    return {};
  };
  
  // Handle chart type change
  const handleChartTypeChange = (event: SelectChangeEvent) => {
    setChartType(event.target.value as 'bar' | 'line' | 'radar');
  };
  
  // Handle unit change
  const handleUnitChange = (event: SelectChangeEvent) => {
    setDisplayUnit(event.target.value);
  };
  
  // Handle category filter change
  const handleCategoryChange = (event: SelectChangeEvent) => {
    setSelectedCategory(event.target.value);
    setSelectedElement(null); // Reset selected element when category changes
  };
  
  // Handle element selection for detailed view
  const handleElementChange = (event: SelectChangeEvent) => {
    setSelectedElement(event.target.value);
  };
  
  // Handle normalize data toggle
  const handleNormalizeToggle = () => {
    setNormalizeData(!normalizeData);
  };
  
  // Handle clear element selection
  const handleClearElementSelection = () => {
    setSelectedElement(null);
  };
  
  // Save chart as image
  const handleSave = () => {
    if (chartInstance && onSaveChart) {
      const dataUrl = chartInstance.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff'
      });
      onSaveChart(dataUrl);
    }
  };
  
  // Handle chart ready event
  const onChartReady = (instance: any) => {
    setChartInstance(instance);
  };
  
  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">
            {selectedElement ? `${selectedElement} Comparison` : title}
            {selectedElement && (
              <IconButton 
                size="small" 
                onClick={handleClearElementSelection} 
                sx={{ ml: 1, verticalAlign: 'middle' }}
              >
                <CompareArrowsIcon fontSize="small" />
              </IconButton>
            )}
          </Typography>
          
          <Box>
            {allowDownload && chartInstance && (
              <Tooltip title="Download Chart">
                <IconButton onClick={handleSave}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}
            
            {onAddProfile && (
              <Tooltip title="Add Profile">
                <IconButton color="primary" onClick={onAddProfile}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        
        {profiles.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              Profiles to Compare
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: 'wrap', gap: 1 }}
            >
              {profiles.map((profile, index) => (
                <Chip
                  key={profile.id}
                  label={profile.name}
                  onDelete={onRemoveProfile ? () => onRemoveProfile(profile.id) : undefined}
                  sx={{
                    bgcolor: `${profile.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}20`,
                    borderColor: profile.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
                    borderWidth: 1,
                    borderStyle: 'solid',
                    mb: 1
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}
        
        <Divider sx={{ mb: 2 }} />
        
        {/* Chart Controls */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid xs={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel id="chart-type-select-label">Chart Type</InputLabel>
              <Select
                labelId="chart-type-select-label"
                id="chart-type-select"
                value={chartType}
                label="Chart Type"
                onChange={handleChartTypeChange}
              >
                <MenuItem value="bar">Bar Chart</MenuItem>
                <MenuItem value="line">Line Chart</MenuItem>
                <MenuItem value="radar">Radar Chart</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid xs={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel id="category-select-label">Category</InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                value={selectedCategory}
                label="Category"
                onChange={handleCategoryChange}
              >
                <MenuItem value="all">All Elements</MenuItem>
                <MenuItem value="macro">Macronutrients</MenuItem>
                <MenuItem value="micro">Micronutrients</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid xs={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel id="element-select-label">Element Focus</InputLabel>
              <Select
                labelId="element-select-label"
                id="element-select"
                value={selectedElement || ''}
                label="Element Focus"
                onChange={handleElementChange}
              >
                <MenuItem value="">
                  <em>All Elements</em>
                </MenuItem>
                {filteredElements.map(element => (
                  <MenuItem key={element} value={element}>
                    {element}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid xs={6} md={3}>
            <Button 
              variant={normalizeData ? "contained" : "outlined"} 
              color={normalizeData ? "primary" : "inherit"}
              size="small"
              onClick={handleNormalizeToggle}
              fullWidth
              sx={{ height: '40px' }} // Match height with selects
            >
              {normalizeData ? "Relative (%)" : "Absolute Values"}
            </Button>
          </Grid>
        </Grid>
        
        {/* Chart Container */}
        <Paper 
          variant="outlined" 
          sx={{ 
            height: 400, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            position: 'relative' 
          }}
        >
          {loading ? (
            <CircularProgress />
          ) : error ? (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography color="error" variant="body1">
                {error}
              </Typography>
            </Box>
          ) : profiles.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <InfoIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                No profiles selected for comparison.
              </Typography>
              {onAddProfile && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={onAddProfile}
                  sx={{ mt: 2 }}
                >
                  Add Profile
                </Button>
              )}
            </Box>
          ) : (
            <Box sx={{ width: '100%', height: '100%' }}>
              <ReactEcharts
                option={getChartOptions()}
                style={{ height: '100%', width: '100%' }}
                onChartReady={onChartReady}
                notMerge={true}
                opts={{ renderer: 'canvas' }}
              />
            </Box>
          )}
        </Paper>
      </CardContent>
    </Card>
  );
};

export default NutrientComparisonGraph;
